/**
 * Curve evaluation and the shape-preserving split (W1-10b core-commands-motion; DESIGN.md §6.2, §6.4, §6.6;
 * rpe-editor-features.md E§4.6, E§4.7).
 *
 * Value at chart time t (seconds), with T_i = sec(key_i.t) and the hold invariant (end_i = k_{i+1}.vIn):
 *   v(t) = k_i.vOut + (k_{i+1}.vIn - k_i.vOut) * f_i((t - T_i) / (T_{i+1} - T_i))   inside segment i
 *   v(t) = k_m.vOut after the last key; the property's lead-in rule before the first key.
 * f_i is Phira's tween of the segment (hold -> 0; tween -> static or clamped by Phira's applicability rule;
 * bezier -> Phira's solver). Effect / video variables use extra.json's rule (slices always applied, no clamping).
 * Progress is in SECONDS, so a segment spanning a BPM change eases in real time.
 *
 * Shape-preserving split at T inside segment i (`insertKeyRec`): the halves get `Ease.splitAt(f_i, p)` with p the
 * progress in seconds, the new key the curve's value v(T). Exactness of the result:
 *  - numbers: the ease's class ('exact' / 'approx' for unclipped Expo/Elastic and Beziers / 'bake'), and each kept
 *    half is checked as Phira plays it in f32 (a half whose ends hold almost the same value loses its shape in
 *    `start + (end - start) * y`: such a half is baked beyond 0.1 % of the range, and a smaller deviation beyond
 *    EXACT_REL_ERROR of the range plus the original's own f32 noise makes the split 'approx' with that deviation).
 *    'Exact' means exact to f32 rounding: the f32 noise counted is that of the values and of the slice's
 *    renormaliser (`playedNoise`; a nearly flat slice such as the tail of an Out curve plays as a staircase whose
 *    steps the halves cannot reproduce point for point). Expo and Elastic, the families DESIGN.md §6.4 tells the
 *    user about, are forgiven only the rounding of the values (`exactNoise`): a staircase change of their flat
 *    slices beyond EXACT_REL_ERROR of the range is reported as 'approx' with its size;
 *  - colours: additionally 'approx' unless v(T) is an exact u8 triple (stored rounded);
 *  - text: a split is exact only where the segment shows ONE text all along (a Switch segment, or start and end
 *    that show the same) and the halves show that text too; a type-on / erase / extend / shrink / counter segment
 *    changes its text at moments that depend on its rounded character count (or formatted number), which each half
 *    rounds anew, so its split is always 'approx'. The displays before and after are compared through their runs of
 *    constant text (textDisplay.ts: dense samples, each character change located to a microsecond, or all of them
 *    to one coarser precision when a long counter has more changes than the evaluation budget can locate), and the
 *    largest shift of a character-change time is reported (`textShiftSec`, shown in the toast in whole
 *    milliseconds; an upper bound, 0 when the characters still appear at the same moments). A degenerate half of
 *    a segment that shows one text becomes linear instead of being baked (its text cannot change);
 *  - 'bake': a half the ease cannot represent (division by zero in Phira's clamped tween, or a measured error
 *    above 0.1 %) becomes linear pieces, values sampled from the original curve. The pieces start on the 1/4-beat
 *    grid and are refined adaptively (cut in the middle on the coarsest power-of-two grid there, worst first) until
 *    each follows the original within 0.1 % of the segment's value range, reaches the 1/1024-beat grid or the half
 *    has 512 pieces; the largest deviation left is measured and reported (`maxRelError`). Text halves keep a fixed
 *    1/32-beat grid (no numeric deviation to refine on); their character-change shift is measured as for text splits.
 * Before the first key the key continues the lead-in (extrapolated first segment, Phira's default, 0 for rpe170
 * speed); after the last key it holds the last value. A key before an extrapolated EASED first segment starts a
 * linear segment, which changes the curve before the first key: that deviation is sampled (old curve against new,
 * from the earlier of beat 0 and the new key up to the old first key) and reported as `approx: 'leadIn'`.
 */

import { Ease, HOLD, LINEAR } from '../ease/index';
import { catalogEntry } from '../ease/catalog';
import { resolveExtra, ZERO_BEZIER } from '../ease/rpeMap';
import { stringTweenDetailed } from '../ease/stringTween';
import type { Ease as EaseValue } from '../ease/types';
import { leadInRule } from '../model/props';
import type { PropSpec, TrackValue, TrackValueType } from '../model/types';
import { Beats } from '../time/beat';
import type { Beat, TempoMap } from '../time/types';
import { indexAt, indexAtOrBefore, newRec, tidy, type KeyRec, type MotionView, type TrackInfo } from './motionTrack';
import { constantText, textRuns, textShift, type TextRun, type TextRuns } from './textDisplay';

export type Exactness = 'exact' | 'approx' | 'bake';

const RANK: Readonly<Record<Exactness, number>> = { exact: 0, approx: 1, bake: 2 };

/** The worse of two exactness classes. */

export function worst(a: Exactness, b: Exactness): Exactness {
  return RANK[a] >= RANK[b] ? a : b;
}

/** Evaluation context of one track. */
export interface CurveCtx {
  readonly tempo: TempoMap;
  readonly info: TrackInfo;
  readonly leadIn: PropSpec['leadIn'];
}

/**
 * Lead-in rule of a track: the property's rule (speed depends on the speed mode); video variables show their
 * default (alpha 1, dim 0) before the first keyframe; effect variables extrapolate like Phira's keyframe lists.
 */
export function curveCtx(view: MotionView & { readonly tempo: TempoMap }, info: TrackInfo): CurveCtx {
  const owner = info.track.owner;
  const leadIn =
    owner.kind === 'line'
      ? leadInRule(owner.prop, view.doc.pkg.speedMode)
      : owner.kind === 'videoVar'
        ? 'default'
        : 'extrapolate';
  return { tempo: view.tempo, info, leadIn };
}

/** f(x) of a segment ease (hold -> 0; variables: extra.json slice rules). */
export function easeY(e: EaseValue, x: number, extra: boolean): number {
  if (e.kind === 'hold') return 0;
  if (e.kind === 'tween' && extra) return Ease.phiraY(resolveExtra(e.id, e.left, e.right), x);
  return Ease.modelY(e, x);
}

/** Unrounded interpolated value: numbers and tuples in f64, text through Phira's string tween. */
export type RawValue = number | readonly number[] | string;

const COUNTER = '%P%';

function lerp(a: number, b: number, y: number): number {
  return a + (b - a) * y;
}

export function interp(a: TrackValue, b: TrackValue, y: number, type: TrackValueType): RawValue {
  switch (type) {
    case 'number':
      return lerp(a as number, b as number, y);
    case 'string': {
      const r = stringTweenDetailed(a as string, b as string, y);
      // A split inside a counter stays a counter (the new key keeps the %P% mark).
      return r.case === 'counter' ? `${COUNTER}${r.text}` : r.text;
    }
    default: {
      const x = a as readonly number[];
      const z = b as readonly number[];
      return x.map((v, i) => lerp(v, z[i] ?? v, y));
    }
  }
}

/** A raw value as stored in a key (numbers tidied, colours rounded to u8) and whether that was exact. */
export function storeValue(
  raw: RawValue,
  type: TrackValueType,
): { readonly value: TrackValue; readonly exact: boolean } {
  switch (type) {
    case 'number': {
      const n = raw as number;
      return Number.isFinite(n) ? { value: tidy(n), exact: true } : { value: 0, exact: false };
    }
    case 'string':
      return { value: raw as string, exact: true };
    case 'vec2': {
      const v = raw as readonly number[];
      return { value: [tidy(v[0] ?? 0), tidy(v[1] ?? 0)], exact: true };
    }
    case 'rgb':
    case 'rgba8': {
      const v = raw as readonly number[];
      let exact = true;
      const out = v.map((c) => {
        const r = Math.min(255, Math.max(0, Math.round(Number.isFinite(c) ? c : 0)));
        if (Math.abs(r - c) > 1e-9) exact = false;
        return r;
      });
      return {
        value:
          type === 'rgb'
            ? [out[0] ?? 0, out[1] ?? 0, out[2] ?? 0]
            : [out[0] ?? 0, out[1] ?? 0, out[2] ?? 0, out[3] ?? 0],
        exact,
      };
    }
  }
}

/**
 * Phira's default before the first key of a 'default' lead-in track (colour white, text "", incline 0, paint -1,
 * video alpha 1 and dim 0).
 */
export function defaultLeadValue(info: TrackInfo): TrackValue {
  const owner = info.track.owner;
  if (owner.kind === 'videoVar') return owner.which === 'alpha' ? 1 : 0;
  switch (info.prop) {
    case 'color':
      return [255, 255, 255];
    case 'text':
      return '';
    case 'paint':
      return -1;
    default:
      return 0;
  }
}

function secOf(ctx: CurveCtx, b: Beat): number {
  return ctx.tempo.secAt(b);
}

/** Progress of `t` in the segment a -> b, in seconds. */
export function progressIn(ctx: CurveCtx, a: Beat, b: Beat, t: Beat): number {
  const ta = secOf(ctx, a);
  const tb = secOf(ctx, b);
  return (secOf(ctx, t) - ta) / (tb - ta);
}

/** Raw value of segment a -> b at progress p. */
export function segmentRaw(a: KeyRec, b: KeyRec, p: number, ctx: CurveCtx): RawValue {
  return interp(a.vOut, b.vIn, easeY(a.ease, p, ctx.info.extra), ctx.info.valueType);
}

const fr = Math.fround;

/**
 * Segment a -> b at progress x as Phira PLAYS it: numbers and vec2 with values, progress and interpolation in f32
 * (`start + (end - start) * y`); text and colours as `segmentRaw`. Where a curve is extrapolated far outside its
 * segment (before the first key), f32 rounding of the values is amplified and decides what is shown.
 */
export function playedRaw(a: KeyRec, b: KeyRec, x: number, ctx: CurveCtx): RawValue {
  const type = ctx.info.valueType;
  if (type !== 'number' && type !== 'vec2') return segmentRaw(a, b, x, ctx);
  const y = easeY(a.ease, fr(x), ctx.info.extra);
  const lerp32 = (p: number, q: number): number => fr(fr(p) + fr(fr(fr(q) - fr(p)) * y));
  if (type === 'number') return lerp32(a.vOut as number, b.vIn as number);
  const p = a.vOut as readonly number[];
  const q = b.vIn as readonly number[];
  return p.map((v, i) => lerp32(v, q[i] ?? v));
}

/** Value before the first key at beat t (lead-in rule), stored form. */
export function leadValue(
  keys: readonly KeyRec[],
  t: Beat,
  ctx: CurveCtx,
): { readonly value: TrackValue; readonly exact: boolean } {
  const k0 = keys[0];
  if (k0 === undefined) return { value: defaultLeadValue(ctx.info), exact: false };
  switch (ctx.leadIn) {
    case 'extrapolate': {
      const k1 = keys[1];
      if (k1 === undefined || k0.ease.kind === 'hold') return { value: k0.vOut, exact: true };
      const p = progressIn(ctx, k0.t, k1.t, t);
      const raw = segmentRaw(k0, k1, p, ctx);
      const s = storeValue(raw, ctx.info.valueType);
      const finite = typeof raw !== 'number' || Number.isFinite(raw);
      if (!finite) return { value: k0.vOut, exact: false };
      const linear = k0.ease.kind === 'tween' && Ease.rpeToTweenId(k0.ease.id) === 2 && !ctx.info.extra;
      return { value: s.value, exact: s.exact && linear };
    }
    case 'default':
      return { value: defaultLeadValue(ctx.info), exact: true };
    case 'zero':
      return { value: 0, exact: true };
    case 'gifLoop':
      return { value: 0, exact: false };
  }
}

/**
 * Value of the curve at beat t (stored form). At a key, 'right' is its leave value and 'left' its arrive value.
 */
export function valueAtBeat(
  keys: readonly KeyRec[],
  t: Beat,
  ctx: CurveCtx,
  side: 'left' | 'right' = 'right',
): TrackValue {
  const i = indexAtOrBefore(keys, t);
  if (i < 0) return leadValue(keys, t, ctx).value;
  const k = keys[i] as KeyRec;
  if (Beats.eq(k.t, t)) {
    if (side === 'right') return k.vOut;
    return i === 0 ? leadValue(keys, t, ctx).value : k.vIn;
  }
  const next = keys[i + 1];
  if (next === undefined) return k.vOut;
  return storeValue(segmentRaw(k, next, progressIn(ctx, k.t, next.t, t), ctx), ctx.info.valueType).value;
}

// ---------------------------------------------------------------------------------------------------------------
// Measuring
// ---------------------------------------------------------------------------------------------------------------

/** Relative deviation up to which a changed curve still counts as exact (f32 rounding; ease split.ts). */
export const EXACT_REL_ERROR = 1e-5;

function componentsOf(v: RawValue): readonly number[] | null {
  if (typeof v === 'string') return null;
  return typeof v === 'number' ? [v] : v;
}

/**
 * Largest component difference of two values; NaN when a component is not finite or the values are texts that
 * differ (texts have no numeric distance).
 */
export function valueDiff(a: RawValue, b: RawValue): number {
  const x = componentsOf(a);
  const y = componentsOf(b);
  if (x === null || y === null) return a === b ? 0 : Number.NaN;
  let m = 0;
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const d = Math.abs((x[i] ?? 0) - (y[i] ?? 0));
    if (!Number.isFinite(d)) return Number.NaN;
    m = Math.max(m, d);
  }
  return m;
}

/** True when every component of the value is finite (texts always are). */
export function rawFinite(v: RawValue): boolean {
  if (typeof v === 'string') return true;
  return typeof v === 'number' ? Number.isFinite(v) : v.every((x) => Number.isFinite(x));
}

/**
 * Relative change counted where one of two curves has a value Phira can show and the other does not (a lead-in
 * extrapolated beyond f32, or a degenerate tween): the whole range.
 */
export const NON_FINITE_CHANGE = 1;

/** Largest per-component span (max - min) of a list of values; non-finite components are ignored. */
export function valueSpan(values: readonly RawValue[]): number {
  const lo: number[] = [];
  const hi: number[] = [];
  for (const v of values) {
    const c = componentsOf(v);
    if (c === null) continue;
    c.forEach((x, i) => {
      if (!Number.isFinite(x)) return;
      lo[i] = Math.min(lo[i] ?? x, x);
      hi[i] = Math.max(hi[i] ?? x, x);
    });
  }
  let span = 0;
  hi.forEach((h, i) => (span = Math.max(span, h - (lo[i] ?? h))));
  return span;
}

// ---------------------------------------------------------------------------------------------------------------
// Baking
// ---------------------------------------------------------------------------------------------------------------

/** Start grid of a baked half (1/4 beat, coarsened to at most 64 pieces); the pieces are then refined. */
export const BAKE_START_DIV = 4;
export const BAKE_START_PIECES = 64;
/** Finest grid a baked piece is cut on (1/1024 beat). */
export const BAKE_FINEST_DIV = 1024;
/** Most linear pieces one baked half may get. */
export const BAKE_HALF_MAX_PIECES = 512;
/** Deviation a baked half aims for: 0.1 % of the segment's value range (the promise of 'approx'). */
export const BAKE_TOLERANCE = 1e-3;
/** Samples per piece when refining a baked half (both ends included). */
const BAKE_PIECE_SAMPLES = 16;
/** Samples per piece of the final measurement that is reported. */
const BAKE_FINAL_SAMPLES = 64;
/** Text halves are baked on a fixed grid: 1/32 beat, coarsened to at most 256 pieces. */
export const BAKE_TEXT_DIV = 32;
export const BAKE_TEXT_MAX_PIECES = 256;

/** Multiples of 1/div strictly inside (a, b), the grid coarsened (div halved) until at most maxPieces pieces. */
export function gridCuts(a: Beat, b: Beat, div: number, maxPieces: number): Beat[] {
  let d = div;
  for (;;) {
    const cuts: Beat[] = [];
    let c = Beats.floorTo(a, d);
    let tooMany = false;
    for (;;) {
      c = Beats.step(c, d, 1);
      if (!Beats.lt(c, b)) break;
      if (Beats.lt(a, c)) cuts.push(c);
      if (cuts.length >= maxPieces) {
        tooMany = true;
        break;
      }
    }
    if (!tooMany || d <= 1) return tooMany ? cuts.slice(0, maxPieces - 1) : cuts;
    d = Math.max(1, Math.floor(d / 2));
  }
}

/** A cut of a baked segment and the value its key gets. */
export interface BakePoint {
  readonly t: Beat;
  readonly v: TrackValue;
}

/**
 * Replaces the segment from keys[i] to keys[i+1] by linear pieces through `points` (strictly inside the segment,
 * ascending). Returns the number of pieces.
 */
export function bakeSegmentWith(keys: KeyRec[], i: number, points: readonly BakePoint[]): number {
  const a = keys[i];
  if (a === undefined || keys[i + 1] === undefined) return 0;
  a.ease = LINEAR;
  keys.splice(i + 1, 0, ...points.map((p) => newRec(p.t, p.v, p.v, LINEAR)));
  return points.length + 1;
}

/**
 * Replaces the segment from keys[i] to keys[i+1] by linear pieces through `cuts` (strictly inside the segment),
 * values from `sample`. Returns the number of pieces.
 */
export function bakeSegmentAt(
  keys: KeyRec[],
  i: number,
  cuts: readonly Beat[],
  sample: (b: Beat) => TrackValue,
): number {
  return bakeSegmentWith(
    keys,
    i,
    cuts.map((t) => ({ t, v: sample(t) })),
  );
}

/**
 * A cut strictly inside (x, y) on the coarsest power-of-two beat grid (up to 1/1024) that has a line in the middle
 * half of the piece, or null when the piece is too short for that grid.
 */
export function pieceCut(x: Beat, y: Beat): Beat | null {
  if (!Beats.lt(x, y)) return null;
  const len = Beats.sub(y, x);
  const quarter = Beats.divInt(len, 4);
  const lo = Beats.add(x, quarter);
  const hi = Beats.sub(y, quarter);
  const mid = Beats.add(x, Beats.divInt(len, 2));
  for (let d = 1; d <= BAKE_FINEST_DIV; d *= 2) {
    const c = Beats.snap(mid, d);
    if (Beats.lte(lo, c) && Beats.lte(c, hi) && Beats.lt(x, c) && Beats.lt(c, y)) return c;
  }
  return null;
}

interface BakeNode extends BakePoint {
  readonly sec: number;
}

/** Largest deviation of the linear piece p -> q (linear in seconds, as Phira plays it) from the original curve. */
function pieceError(
  p: BakeNode,
  q: BakeNode,
  rawAt: (sec: number) => RawValue,
  type: TrackValueType,
  samples = BAKE_PIECE_SAMPLES,
): number {
  let m = 0;
  for (let k = 0; k <= samples; k++) {
    const x = k / samples;
    const d = valueDiff(rawAt(p.sec + (q.sec - p.sec) * x), interp(p.v, q.v, x, type));
    if (Number.isFinite(d)) m = Math.max(m, d);
  }
  return m;
}

/** Cuts of an adaptively baked half and the largest deviation (absolute, in value units) left. */
export interface BakedHalf {
  readonly points: readonly BakePoint[];
  readonly maxError: number;
}

/**
 * Cuts of a baked half [s, e] whose linear pieces follow `rawAt` (the original curve, by chart second) within
 * `tolerance` (absolute value units): the 1/4-beat start grid, then every piece measured above the tolerance is
 * cut in its middle (`pieceCut`), worst first, until all pieces are within the tolerance, too short to cut, or the
 * half has BAKE_HALF_MAX_PIECES pieces. `vs` / `ve` are the values of the half's edge keys. Numeric value types
 * only (numbers, vec2, colours).
 */
export function adaptiveBakeCuts(
  s: Beat,
  e: Beat,
  vs: TrackValue,
  ve: TrackValue,
  ctx: CurveCtx,
  rawAt: (sec: number) => RawValue,
  tolerance: number,
): BakedHalf {
  const type = ctx.info.valueType;
  const node = (t: Beat): BakeNode => {
    const sec = secOf(ctx, t);
    return { t, sec, v: storeValue(rawAt(sec), type).value };
  };
  const nodes: BakeNode[] = [
    { t: s, sec: secOf(ctx, s), v: vs },
    ...gridCuts(s, e, BAKE_START_DIV, BAKE_START_PIECES).map(node),
    { t: e, sec: secOf(ctx, e), v: ve },
  ];
  const errs: number[] = [];
  for (let j = 0; j + 1 < nodes.length; j++) {
    errs.push(pieceError(nodes[j] as BakeNode, nodes[j + 1] as BakeNode, rawAt, type));
  }
  for (;;) {
    const budget = BAKE_HALF_MAX_PIECES - errs.length;
    if (budget <= 0) break;
    const over = errs
      .map((err, j) => ({ err, j }))
      .filter((x) => x.err > tolerance)
      .sort((a, b) => b.err - a.err || a.j - b.j);
    const cuts: { j: number; c: Beat }[] = [];
    for (const { j } of over) {
      if (cuts.length >= budget) break;
      const c = pieceCut((nodes[j] as BakeNode).t, (nodes[j + 1] as BakeNode).t);
      if (c !== null) cuts.push({ j, c });
    }
    if (cuts.length === 0) break;
    // Back to front, so the indices of the pieces still to cut stay valid.
    cuts.sort((a, b) => b.j - a.j);
    for (const { j, c } of cuts) {
      const n = node(c);
      nodes.splice(j + 1, 0, n);
      errs.splice(
        j,
        1,
        pieceError(nodes[j] as BakeNode, n, rawAt, type),
        pieceError(n, nodes[j + 2] as BakeNode, rawAt, type),
      );
    }
  }
  // The reported deviation is measured again, four times as densely as the refinement looked.
  let maxError = 0;
  for (let j = 0; j + 1 < nodes.length; j++) {
    const err = pieceError(nodes[j] as BakeNode, nodes[j + 1] as BakeNode, rawAt, type, BAKE_FINAL_SAMPLES);
    maxError = Math.max(maxError, err, errs[j] ?? 0);
  }
  return { points: nodes.slice(1, -1).map((n) => ({ t: n.t, v: n.v })), maxError };
}

// ---------------------------------------------------------------------------------------------------------------
// Insert a key (shape-preserving split)
// ---------------------------------------------------------------------------------------------------------------

/**
 * What an 'approx' insertion changed besides the usual curve split (absent: the split itself, see `maxRelError`):
 *  - 'leadIn': the curve before the first key (a linear segment replaces Phira's eased extrapolation);
 *  - 'gif': where the GIF plays on its own at natural speed (a key after the last key, in a gap or before the first);
 *  - 'linearHalf': a key with an explicit value where a half's curve could not be kept (that half is linear);
 *  - 'leadInShift': a time operation (insert / remove time) moved the first segment of a track whose lead-in Phira
 *    extrapolates from it, which changed the curve before its first key (motionTime.ts `leadInShiftReport`).
 */
export type ApproxKind = 'leadIn' | 'gif' | 'linearHalf' | 'leadInShift';

/** How exactly one shape-preserving split (or the worst of several) kept the curve. */
export interface SplitReport {
  readonly exactness: Exactness;
  /**
   * Largest measured deviation of the new curve from the old one, relative to the value range: the ease split
   * measurement, the baked pieces' deviation, or the lead-in change (0 when exact or not measurable).
   */
  readonly maxRelError: number;
  readonly approx?: ApproxKind;
  /**
   * Set when splitting the FIRST segment of a track that extrapolates it backwards (an imported lead-in) also
   * changed the visible curve before the first key, while the split itself is approximate or baked: that change,
   * relative like `maxRelError`. (An otherwise exact split reports it as `approx: 'leadIn'` instead.)
   */
  readonly leadInRelError?: number;
  /**
   * Text tracks: the largest shift (chart seconds) of a moment the shown text changes, between the old curve and
   * the new one (`textShift`); `maxRelError` is then that shift relative to the duration of the changed segment.
   */
  readonly textShiftSec?: number;
}

/** The report of splits that kept the curve exactly (or of no split at all). */
export const EXACT_SPLIT: SplitReport = Object.freeze({ exactness: 'exact', maxRelError: 0 });

/** The split that changed its curve the most: the worse exactness class first, then the larger deviation. */
export function worseSplit(a: SplitReport, b: SplitReport): SplitReport {
  if (a.exactness !== b.exactness) return worst(a.exactness, b.exactness) === a.exactness ? a : b;
  const dev = (r: SplitReport): number => Math.max(r.maxRelError, r.leadInRelError ?? 0);
  return dev(b) > dev(a) ? b : a;
}

export interface InsertOutcome extends SplitReport {
  /** Index of the key at t after the insertion. */
  readonly index: number;
  /** False when a key already existed at t. */
  readonly created: boolean;
}

/** Sets a key's value as part 'both' would: continuous, except after a Stay (only the leave value). */
export function setBoth(keys: KeyRec[], i: number, value: TrackValue): void {
  const k = keys[i];
  if (k === undefined) return;
  const prev = keys[i - 1];
  k.vOut = value;
  if (prev === undefined || prev.ease.kind !== 'hold') k.vIn = value;
}

/**
 * What a display "shows" where Phira panics instead (a type-on or erase whose eased progress leaves [0, 1], e.g. an
 * Elastic or Back curve, slices its text past the end, F§9.3): a value no text can equal, so a split that moves
 * where Phira panics counts as a changed display.
 */
export const PANIC_DISPLAY = '\u0000panic';

/**
 * Text Phira shows on segment a -> b at progress x: its string tween of the eased progress (a Stay is lowered to a
 * constant event, start = end = its value, F§9.3); PANIC_DISPLAY where Phira panics.
 */
export function displayedText(a: KeyRec, b: KeyRec, x: number, extra: boolean): string {
  const r =
    a.ease.kind === 'hold'
      ? stringTweenDetailed(a.vOut as string, a.vOut as string, x)
      : stringTweenDetailed(a.vOut as string, b.vIn as string, easeY(a.ease, x, extra));
  return r.panics ? PANIC_DISPLAY : r.text;
}

/**
 * Text shown at chart second `sec` on the consecutive keys `keys` (at least two; `secs` their chart seconds): the
 * segment that contains `sec` (the first one before it, the last one from its start on).
 */
export function displayOn(keys: readonly KeyRec[], secs: readonly number[], sec: number, extra: boolean): string {
  const last = keys.length - 2;
  if (last < 0) return keys[0] === undefined ? '' : displayedText(keys[0], keys[0], 0, extra);
  let lo = 0;
  let hi = last;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if ((secs[mid] as number) <= sec) lo = mid;
    else hi = mid - 1;
  }
  const s0 = secs[lo] as number;
  const s1 = secs[lo + 1] as number;
  return displayedText(keys[lo] as KeyRec, keys[lo + 1] as KeyRec, (sec - s0) / (s1 - s0), extra);
}

/** Runs of the text the original segment `a` -> `b` shows between its keys. */
function originalTextRuns(a: KeyRec, b: KeyRec, ctx: CurveCtx): TextRuns {
  const ta = secOf(ctx, a.t);
  const tb = secOf(ctx, b.t);
  return textRuns((sec) => displayedText(a, b, (sec - ta) / (tb - ta), ctx.info.extra), ta, tb);
}

/** How a text split kept what the segment shows (see the file header). */
interface TextSplitCheck {
  /** The segment showed one text all along and the keys that replaced it show that text too. */
  readonly exact: boolean;
  /** Largest shift (seconds) of a character-change time. */
  readonly shiftSec: number;
  /** That shift relative to the segment's duration. */
  readonly rel: number;
}

/**
 * Compares what the original text segment `original` -> `b` showed (`before`, its runs) with what the keys that
 * replaced it show: keys[i] up to `b` (two halves, or baked pieces).
 */
function textSplitCheck(
  before: TextRuns,
  original: KeyRec,
  b: KeyRec,
  keys: readonly KeyRec[],
  i: number,
  ctx: CurveCtx,
): TextSplitCheck {
  const ta = secOf(ctx, original.t);
  const tb = secOf(ctx, b.t);
  const j = keys.indexOf(b);
  const part = keys.slice(i, j < 0 ? i + 2 : j + 1);
  const secs = part.map((k) => secOf(ctx, k.t));
  const after = textRuns((sec) => displayOn(part, secs, sec, ctx.info.extra), ta, tb);
  const shiftSec = textShift(before, after, tb);
  const exact =
    constantText(before) && constantText(after) && (before.runs[0] as TextRun).text === (after.runs[0] as TextRun).text;
  return { exact, shiftSec, rel: tb > ta ? shiftSec / (tb - ta) : 0 };
}

/** An insertion's outcome for a text split from its check (exact, or `approx` / `bake` with the shift). */
function textOutcome(base: InsertOutcome, check: TextSplitCheck): InsertOutcome {
  if (check.exact && base.exactness !== 'bake') return { ...base, exactness: 'exact', maxRelError: 0 };
  return {
    ...base,
    exactness: base.exactness === 'bake' ? 'bake' : 'approx',
    maxRelError: check.rel,
    textShiftSec: check.shiftSec,
  };
}

function unlink(k: KeyRec, unlinked: Set<number>): void {
  if (k.link !== 0) {
    unlinked.add(k.link);
    k.link = 0;
  }
}

/** Samples of the lead-in measurement (each of the visible lead-in and the old first segment). */
const LEAD_IN_SAMPLES = 512;

/**
 * Relative change of the curve before the old first key `oldA` on a track whose first segment Phira extrapolates
 * backwards, when the first segment becomes `newA` -> `newB` (a new key before it, or a split of it): old against
 * new curve over the visible lead-in, from beat 0 (chart time never goes below 0) up to `oldA`, relative to the
 * old curve's span over that range and its first segment. 0 when no lead-in is visible. Both curves are evaluated
 * as Phira plays them (`playedRaw`); where only one of them is finite the change counts as the whole range
 * (`NON_FINITE_CHANGE`).
 */
function leadInDeviation(oldA: KeyRec, oldB: KeyRec, newA: KeyRec, newB: KeyRec, ctx: CurveCtx): number {
  const t0 = secOf(ctx, oldA.t);
  const t1 = secOf(ctx, oldB.t);
  const lo = secOf(ctx, Beats.ZERO);
  if (!(t0 > lo)) return 0;
  const na = secOf(ctx, newA.t);
  const nb = secOf(ctx, newB.t);
  const oldAt = (sec: number): RawValue => playedRaw(oldA, oldB, (sec - t0) / (t1 - t0), ctx);
  const newAt = (sec: number): RawValue => playedRaw(newA, newB, (sec - na) / (nb - na), ctx);
  const seen: RawValue[] = [oldA.vOut, oldB.vIn];
  let dev = 0;
  let broken = false;
  for (let j = 0; j <= LEAD_IN_SAMPLES; j++) {
    const sec = lo + ((t0 - lo) * j) / LEAD_IN_SAMPLES;
    const was = oldAt(sec);
    const now = newAt(sec);
    seen.push(was, oldAt(t0 + ((t1 - t0) * j) / LEAD_IN_SAMPLES));
    if (rawFinite(was) !== rawFinite(now)) broken = true;
    const d = valueDiff(was, now);
    if (Number.isFinite(d)) dev = Math.max(dev, d);
  }
  const span = valueSpan(seen);
  const rel = span > 0 ? dev / span : 0;
  return broken ? Math.max(rel, NON_FINITE_CHANGE) : rel;
}

/** Folds a measured lead-in change into an insertion's outcome (see `InsertOutcome.leadInRelError`). */
function withLeadIn(out: InsertOutcome, rel: number): InsertOutcome {
  if (!(rel > EXACT_REL_ERROR)) return out;
  if (out.exactness === 'exact') return { ...out, exactness: 'approx', maxRelError: rel, approx: 'leadIn' };
  return { ...out, leadInRelError: rel };
}

/** Inserts a key before the first key (continuing the lead-in rule); see `insertKeyRec`. */
function insertBeforeFirst(keys: KeyRec[], t: Beat, ctx: CurveCtx, value: TrackValue | undefined): InsertOutcome {
  const k0 = keys[0] as KeyRec;
  const k1 = keys[1];
  const lead = leadValue(keys, t, ctx);
  const extrapolated = ctx.leadIn === 'extrapolate' && k0.ease.kind !== 'hold';
  const rec = newRec(t, value ?? lead.value, value ?? lead.value, extrapolated ? LINEAR : HOLD);
  // An explicit value changes the curve on purpose: nothing was approximated.
  const rel =
    value === undefined && extrapolated && !lead.exact && k1 !== undefined ? leadInDeviation(k0, k1, rec, k0, ctx) : 0;
  keys.unshift(rec);
  const base = { index: 0, created: true } as const;
  // Before its first key a GIF loops on its own; the new key takes that span over, whatever its value.
  if (ctx.leadIn === 'gifLoop') return { ...base, exactness: 'approx', maxRelError: 0, approx: 'gif' };
  if (value !== undefined || lead.exact) return { ...base, exactness: 'exact', maxRelError: 0 };
  if (rel <= EXACT_REL_ERROR) return { ...base, exactness: 'exact', maxRelError: rel };
  return { ...base, exactness: 'approx', maxRelError: rel, approx: 'leadIn' };
}

/** The original curve of a segment being split, as Phira plays it, and the error budget of its halves. */
interface SplitSource {
  /** Chart seconds of the segment's start and end. */
  readonly ta: number;
  readonly tb: number;
  /** Unrounded value of the original segment at chart second `sec`. */
  readonly rawAt: (sec: number) => RawValue;
  /** |end - start| of the segment (largest component); deviations are relative to it. */
  readonly span: number;
  /** Absolute deviation a kept or baked half may have (BAKE_TOLERANCE of the span, colour rounding). */
  readonly tolerance: number;
}

function splitSource(original: KeyRec, b: KeyRec, ctx: CurveCtx): SplitSource {
  const type = ctx.info.valueType;
  const ta = secOf(ctx, original.t);
  const tb = secOf(ctx, b.t);
  const spanRaw = valueDiff(original.vOut, b.vIn);
  const span = Number.isFinite(spanRaw) ? spanRaw : 0;
  // Colours are stored as whole channel values: half a step of rounding at either end is no error to refine.
  const quantum = type === 'rgb' || type === 'rgba8' ? 1 : 0;
  return {
    ta,
    tb,
    rawAt: (sec) => segmentRaw(original, b, (sec - ta) / (tb - ta), ctx),
    span,
    tolerance: Math.max(BAKE_TOLERANCE * span, quantum, 1e-9),
  };
}

/** Uniform samples of the check of a kept Bezier half (both ends included). */
const HALF_CHECK_SAMPLES = 128;
/**
 * Phira's Bezier solver starts from a 21-entry table of x(t) and picks the entry by `x / (1/20)`; where it does not
 * converge its output jumps at those seams (progress k/20). Both curves are sampled just before and after the
 * seams of either, where a narrow mismatch hides between uniform samples.
 */
const SOLVER_SEAMS = 20;
const SEAM_OFFSETS: readonly number[] = [-1e-4, -1e-6, 1e-6, 1e-4];

/**
 * Largest deviation (absolute) of the kept half `from` -> `to` from the original curve, sampled densely in
 * seconds through the same evaluation Phira uses (its Bezier solver included).
 */
function halfDeviation(from: KeyRec, to: KeyRec, src: SplitSource, ctx: CurveCtx): number {
  const ts = secOf(ctx, from.t);
  const te = secOf(ctx, to.t);
  const xs: number[] = [];
  for (let j = 0; j <= HALF_CHECK_SAMPLES; j++) xs.push(j / HALF_CHECK_SAMPLES);
  for (let k = 1; k < SOLVER_SEAMS; k++) {
    const own = k / SOLVER_SEAMS;
    const orig = (src.ta + (src.tb - src.ta) * own - ts) / (te - ts);
    for (const d of SEAM_OFFSETS) xs.push(own + d, orig + d);
  }
  let m = 0;
  for (const x of xs) {
    if (!(x >= 0 && x <= 1)) continue;
    const d = valueDiff(src.rawAt(ts + (te - ts) * x), segmentRaw(from, to, x, ctx));
    if (Number.isFinite(d)) m = Math.max(m, d);
  }
  return m;
}

/** Uniform samples of the check of a kept half as Phira plays it (both ends included). */
const PLAYED_CHECK_SAMPLES = 64;
/** f32 rounding allowed in that check, relative to the largest value of the segment (a few f32 steps). */
export const PLAYED_REL_NOISE = 2 ** -20;
/** f32 step of a tween value counted as renormaliser noise (every tween stays below 2 in magnitude: 2^-23). */
const RENORM_STEP = 2 ** -23;

/**
 * f32 noise of a segment's eased progress as Phira plays it, relative to the segment's value range, from the
 * renormaliser of its clamped tween: y = (f(L + (R - L) x) - f(L)) / (f(R) - f(L)) rounds f to f32 steps (at most
 * 2^-23), and a nearly flat slice (f(R) close to f(L), e.g. the tail of an Out curve) divides those steps by its
 * small span: the curve Phira shows is a staircase of that step height. A split half of such a slice rounds at
 * other points (its own staircase has the same height in value units), so the two curves differ by up to a step
 * of each, however exactly the ease itself was split. 0 for eases without a renormaliser (Stay, Beziers, unsliced
 * tweens, whose span is about 1, so the step is far below EXACT_REL_ERROR).
 */
export function renormNoise(e: EaseValue, extra: boolean): number {
  if (e.kind !== 'tween') return 0;
  const desc = extra ? resolveExtra(e.id, e.left, e.right) : Ease.resolveRpe(e.id, e.left, e.right, 0, ZERO_BEZIER);
  if (desc.kind !== 'clamped') return 0;
  const span = Math.abs(fr(Ease.staticY(desc.tween, desc.r) - Ease.staticY(desc.tween, desc.l)));
  return span > 0 && Number.isFinite(span) ? RENORM_STEP / span : 0;
}

/** Renormaliser noise of segment a -> b in value units (`renormNoise` of its value range). */
export function renormNoiseOf(a: KeyRec, b: KeyRec, extra: boolean): number {
  const range = valueDiff(a.vOut, b.vIn);
  return Number.isFinite(range) ? range * renormNoise(a.ease, extra) : 0;
}

/**
 * Absolute f32 noise between segment a -> b and a curve that replaces it (`c` -> `d`: a split half or a reversed
 * piece), both as Phira plays them: rounding of the values (PLAYED_REL_NOISE of their magnitude) and a step of
 * each tween's renormaliser (`renormNoiseOf`). Two curves that agree within this plus EXACT_REL_ERROR of the
 * range count as the same curve ('exact' to f32 rounding).
 */
export function playedNoise(a: KeyRec, b: KeyRec, c: KeyRec, d: KeyRec, extra: boolean): number {
  return (
    magnitude(a.vOut, b.vIn, c.vOut, d.vIn) * PLAYED_REL_NOISE + renormNoiseOf(a, b, extra) + renormNoiseOf(c, d, extra)
  );
}

/** True for Expo and Elastic tweens (RPE easing types; extra.json uses the same numbering). */
export function isExpoOrElastic(e: EaseValue): boolean {
  if (e.kind !== 'tween') return false;
  const family = catalogEntry(e.id)?.family;
  return family === 'expo' || family === 'elastic';
}

/**
 * The f32 noise forgiven when a replaced curve (`c` -> `d`) is still labelled 'exact' against segment a -> b:
 * `playedNoise`, except for Expo and Elastic, which DESIGN.md §6.4 names as the families the user is told about.
 * Their nearly flat slices (the tail of OutElastic [0.9, 1], InOutElastic [0.9, 1]) play as a renormaliser
 * staircase that a split or reversed slice rounds at other points, so the curve Phira shows moves by up to a few
 * 1e-4 of the range: that change is reported (`staircaseChange`; only the rounding of the values is forgiven). The
 * bake tolerance still allows the full `playedNoise`: a half is never baked for a staircase it cannot avoid.
 */
export function exactNoise(a: KeyRec, b: KeyRec, c: KeyRec, d: KeyRec, extra: boolean): number {
  if (!isExpoOrElastic(a.ease) && !isExpoOrElastic(c.ease)) return playedNoise(a, b, c, d, extra);
  return magnitude(a.vOut, b.vIn, c.vOut, d.vIn) * PLAYED_REL_NOISE;
}

/** Spacing of f32 values around |v| (the unit in the last place). */
function f32Ulp(v: number): number {
  const m = Math.abs(v);
  if (!(m >= 2 ** -126) || !Number.isFinite(m)) return 2 ** -149;
  return 2 ** (Math.floor(Math.log2(m)) - 23);
}

/** Samples of a slice when looking for the largest |f| its renormaliser rounds (Elastic oscillates inside). */
const STAIRCASE_SAMPLES = 16;

/**
 * Height of one step of the renormaliser staircase of an Expo / Elastic slice, relative to the segment's value
 * range: the f32 spacing of f where it is largest over the slice, divided by f(R) - f(L) (`renormNoise` is the
 * coarser bound 2^-23 used for every family; near 0 f32 is much finer, so the tail of an In curve has almost no
 * staircase). 0 for other eases and for unsliced tweens (no renormaliser).
 */
export function staircaseStep(e: EaseValue, extra: boolean): number {
  if (e.kind !== 'tween' || !isExpoOrElastic(e)) return 0;
  const desc = extra ? resolveExtra(e.id, e.left, e.right) : Ease.resolveRpe(e.id, e.left, e.right, 0, ZERO_BEZIER);
  if (desc.kind !== 'clamped') return 0;
  const fl = fr(Ease.staticY(desc.tween, desc.l));
  const span = Math.abs(fr(Ease.staticY(desc.tween, desc.r) - fl));
  if (!(span > 0) || !Number.isFinite(span)) return 0;
  let ulp = 0;
  for (let k = 0; k <= STAIRCASE_SAMPLES; k++) {
    const f = Ease.staticY(desc.tween, fr(desc.l + ((desc.r - desc.l) * k) / STAIRCASE_SAMPLES));
    if (Number.isFinite(f)) ulp = Math.max(ulp, f32Ulp(f));
  }
  return ulp / span;
}

/**
 * How far (value units) the curve Phira shows moves when the sliced Expo / Elastic segment a -> b is replaced by a
 * split half or a reversed slice of it: one step of its renormaliser staircase (`staircaseStep` of its value
 * range). The replacement has steps of the same height in values (its slice spans the matching part of f) placed
 * at other moments, and the narrow bands where the two staircases disagree fall between the samples of a
 * measurement, so the step itself is reported (an upper bound of the change). 0 for other eases.
 */
export function staircaseChange(a: KeyRec, b: KeyRec, extra: boolean): number {
  const range = valueDiff(a.vOut, b.vIn);
  return Number.isFinite(range) ? range * staircaseStep(a.ease, extra) : 0;
}

/**
 * Largest deviation (absolute) of the kept half `from` -> `to` from the original segment `orig` -> `b`, both as
 * Phira PLAYS them (`playedRaw`: values, progress and `start + (end - start) * y` in f32). An ease split is exact on
 * paper, but a half whose ends hold almost the same value (an ease that comes back to its start, e.g. a Back or
 * Elastic curve, split where it returns) keeps its shape only in its eased progress: f32 rounds `end - start` to a
 * few steps or to 0, and Phira shows a flat or stepped half.
 */
function playedHalfDeviation(from: KeyRec, to: KeyRec, orig: KeyRec, b: KeyRec, ctx: CurveCtx): number {
  const ts = secOf(ctx, from.t);
  const te = secOf(ctx, to.t);
  const ta = secOf(ctx, orig.t);
  const tb = secOf(ctx, b.t);
  let m = 0;
  for (let j = 0; j <= PLAYED_CHECK_SAMPLES; j++) {
    const x = j / PLAYED_CHECK_SAMPLES;
    const sec = ts + (te - ts) * x;
    const d = valueDiff(playedRaw(orig, b, (sec - ta) / (tb - ta), ctx), playedRaw(from, to, x, ctx));
    if (Number.isFinite(d)) m = Math.max(m, d);
  }
  return m;
}

/** Largest absolute component of the given values (0 for texts). */
export function magnitude(...values: readonly TrackValue[]): number {
  let m = 0;
  for (const v of values) {
    const c = componentsOf(v);
    if (c !== null) for (const x of c) if (Number.isFinite(x)) m = Math.max(m, Math.abs(x));
  }
  return m;
}

/**
 * Bakes the halves of a split that the split cannot represent (keys[i] -> mid -> b, mid at index i + 1). Returns
 * the largest deviation left relative to the segment's value range.
 */
function bakeHalves(
  keys: KeyRec[],
  i: number,
  b: KeyRec,
  halves: ReadonlySet<'left' | 'right'>,
  src: SplitSource,
  ctx: CurveCtx,
): number {
  const type = ctx.info.valueType;
  const a = keys[i] as KeyRec;
  const mid = keys[i + 1] as KeyRec;
  const sample = (c: Beat): TrackValue => storeValue(src.rawAt(secOf(ctx, c)), type).value;
  let worstErr = 0;
  const bake = (k: number, s: Beat, e: Beat, vs: TrackValue, ve: TrackValue): void => {
    if (type === 'string') {
      bakeSegmentAt(keys, k, gridCuts(s, e, BAKE_TEXT_DIV, BAKE_TEXT_MAX_PIECES), sample);
      return;
    }
    const r = adaptiveBakeCuts(s, e, vs, ve, ctx, src.rawAt, src.tolerance);
    bakeSegmentWith(keys, k, r.points);
    if (src.span > 0) worstErr = Math.max(worstErr, r.maxError / src.span);
  };
  // Right half first: baking it does not move the left half's keys.
  if (halves.has('right')) bake(i + 1, mid.t, b.t, mid.vOut, b.vIn);
  if (halves.has('left')) bake(i, a.t, mid.t, a.vOut, mid.vIn);
  return worstErr;
}

/**
 * Inserts a key at t into `keys` (mutated), shape-preserving; see the file header. `value` overrides the new
 * key's value (the halves still get the split eases). Curves split here leave their link group (`unlinked`); a split
 * Stay keeps its group (same values, same curve).
 */
export function insertKeyRec(
  keys: KeyRec[],
  t: Beat,
  ctx: CurveCtx,
  unlinked: Set<number>,
  value?: TrackValue,
): InsertOutcome {
  const type = ctx.info.valueType;
  const isGif = ctx.info.prop === 'gif';
  if (keys.length === 0) {
    const v = value ?? defaultLeadValue(ctx.info);
    keys.push(newRec(t, v, v, LINEAR));
    return { index: 0, exactness: 'exact', created: true, maxRelError: 0 };
  }
  const at = indexAt(keys, t);
  if (at >= 0) {
    if (value !== undefined) setBoth(keys, at, value);
    return { index: at, exactness: 'exact', created: false, maxRelError: 0 };
  }
  const i = indexAtOrBefore(keys, t);
  // Before the first key: continue the lead-in.
  if (i < 0) return insertBeforeFirst(keys, t, ctx, value);
  const a = keys[i] as KeyRec;
  const b = keys[i + 1];
  if (b === undefined) {
    // After the last key: hold the last value (a GIF played on after its last key). The last key starts a segment
    // now; a link group it still named had no segment of it, so the new segment does not join it. Without an
    // explicit value that segment is constant: it is linear, as any stored ease would only show where Phira
    // extrapolates it backwards (a single imported key), and there `v + 0 * f(x)` is not finite for every ease.
    unlink(a, unlinked);
    if (a.ease.kind === 'hold' || value === undefined) a.ease = LINEAR;
    const v = value ?? a.vOut;
    keys.push(newRec(t, v, v, LINEAR));
    const index = keys.length - 1;
    return isGif
      ? { index, exactness: 'approx', created: true, maxRelError: 0, approx: 'gif' }
      : { index, exactness: 'exact', created: true, maxRelError: 0 };
  }
  if (a.ease.kind === 'hold') {
    // The first half is the same Stay (same values, same curve, still a gap when it was one), so it keeps its
    // link group. A GIF gap auto-plays; the second half becomes an explicit Stay, which freezes the GIF there.
    const gap = a.ease.gap === true;
    const mid = newRec(t, a.vOut, value ?? a.vOut, HOLD);
    keys.splice(i + 1, 0, mid);
    return isGif && gap
      ? { index: i + 1, exactness: 'approx', created: true, maxRelError: 0, approx: 'gif' }
      : { index: i + 1, exactness: 'exact', created: true, maxRelError: 0 };
  }
  unlink(a, unlinked);
  const pRaw = progressIn(ctx, a.t, b.t, t);
  const p = Math.min(Math.max(pRaw, 1e-12), 1 - 1e-12);
  const original: KeyRec = { ...a };
  const split = Ease.splitAt(a.ease, p);
  const stored = storeValue(segmentRaw(a, b, p, ctx), type);
  let exactness: Exactness = split.exactness;
  const mid = newRec(t, stored.value, stored.value, split.right);
  a.ease = split.left;
  if (value !== undefined) {
    mid.vIn = value;
    mid.vOut = value;
  }
  keys.splice(i + 1, 0, mid);
  const halves = new Set<'left' | 'right'>(split.exactness === 'bake' ? (split.bakeHalves ?? ['left', 'right']) : []);
  if (value !== undefined && halves.size > 0) {
    // The curve was replaced on purpose: a half the split cannot keep becomes a straight line.
    if (halves.has('left')) a.ease = LINEAR;
    if (halves.has('right')) mid.ease = LINEAR;
    return {
      index: indexAt(keys, t),
      exactness: 'approx',
      created: true,
      maxRelError: split.maxRelError,
      approx: 'linearHalf',
    };
  }
  // Text: what the segment showed, measured before a half is baked.
  const textBefore = value === undefined && type === 'string' ? originalTextRuns(original, b, ctx) : null;
  if (textBefore !== null && constantText(textBefore) && halves.size > 0) {
    // A segment that shows one text all along needs no pieces: a half Phira would divide by zero in becomes
    // linear, which shows the same text (checked below with the other half).
    if (halves.has('left')) a.ease = LINEAR;
    if (halves.has('right')) mid.ease = LINEAR;
    halves.clear();
    exactness = 'exact';
  }
  const src = splitSource(original, b, ctx);
  let maxRelError = split.maxRelError;
  if (value === undefined && type !== 'string' && original.ease.kind === 'bezier') {
    // Phira's Bezier solver can jump between nearby inputs, which the ease split's samples may miss: each kept
    // half is checked densely, and one that strays beyond the tolerance is baked as well.
    for (const [h, from, to] of [
      ['left', a, mid],
      ['right', mid, b],
    ] as const) {
      if (halves.has(h)) continue;
      const dev = halfDeviation(from, to, src, ctx);
      if (dev > src.tolerance) halves.add(h);
      else if (src.span > 0) maxRelError = Math.max(maxRelError, dev / src.span);
    }
  }
  if (value === undefined && (type === 'number' || type === 'vec2')) {
    // Each kept half as Phira plays it (f32): a half whose two ends are almost equal loses its shape there. Beyond
    // the bake tolerance it is baked; a deviation beyond the original's own f32 noise (its values and its slice's
    // renormaliser, `playedNoise`; Expo / Elastic: its values only, `exactNoise`, and a staircase step counts as a
    // change, `staircaseChange`) plus EXACT_REL_ERROR of the range still makes the split approximate (the ease
    // split's own measurement checks a unit segment, not these values).
    for (const [h, from, to] of [
      ['left', a, mid],
      ['right', mid, b],
    ] as const) {
      if (halves.has(h)) continue;
      const extra = ctx.info.extra;
      const noise = playedNoise(original, b, from, to, extra);
      const dev = playedHalfDeviation(from, to, original, b, ctx);
      if (dev > Math.max(src.tolerance, noise)) {
        halves.add(h);
        continue;
      }
      const shown = Math.max(dev, staircaseChange(original, b, extra));
      if (src.span > 0 && shown > exactNoise(original, b, from, to, extra) + EXACT_REL_ERROR * src.span) {
        exactness = worst(exactness, 'approx');
        maxRelError = Math.max(maxRelError, shown / src.span);
      }
    }
  }
  // Before the first key Phira extrapolates the first segment's curve: a split of that segment (an imported
  // lead-in, or a first key after beat 0) also changes what is shown there. Measured once the halves are final.
  const leadIn = (out: InsertOutcome): InsertOutcome => {
    const first = keys[0];
    const second = keys[1];
    if (i !== 0 || value !== undefined || ctx.leadIn !== 'extrapolate' || first === undefined || second === undefined)
      return out;
    return withLeadIn(out, leadInDeviation(original, b, first, second, ctx));
  };
  if (halves.size > 0) {
    const bakeErr = bakeHalves(keys, i, b, halves, src, ctx);
    const baked: InsertOutcome = {
      index: indexAt(keys, t),
      exactness: 'bake',
      created: true,
      maxRelError: Math.max(maxRelError, bakeErr),
    };
    return leadIn(
      textBefore === null ? baked : textOutcome(baked, textSplitCheck(textBefore, original, b, keys, i, ctx)),
    );
  }
  const kept: InsertOutcome = { index: indexAt(keys, t), exactness, created: true, maxRelError };
  if (textBefore !== null) return leadIn(textOutcome(kept, textSplitCheck(textBefore, original, b, keys, i, ctx)));
  // With an explicit value the halves only carry the split eases; the colour check compares against the original
  // values, which the key no longer has.
  if (value === undefined && !stored.exact) return leadIn({ ...kept, exactness: worst(exactness, 'approx') });
  return leadIn(kept);
}

/**
 * RPE's split (E§4.7, palette only): the value at T is interpolated LINEARLY in time between the segment's start
 * and end values and both halves keep the original easing (this changes the motion). Returns the new key's index,
 * or -1 when t is not strictly inside a segment.
 */
export function rpeLinearSplit(keys: KeyRec[], t: Beat, ctx: CurveCtx, unlinked: Set<number>): number {
  const i = indexAtOrBefore(keys, t);
  if (i < 0 || indexAt(keys, t) >= 0) return -1;
  const a = keys[i] as KeyRec;
  const b = keys[i + 1];
  if (b === undefined) return -1;
  unlink(a, unlinked);
  const hold = a.ease.kind === 'hold';
  // A split gap is no longer a gap.
  if (hold) a.ease = HOLD;
  const frac = Beats.toF64(Beats.sub(t, a.t)) / Beats.toF64(Beats.sub(b.t, a.t));
  const v = hold ? a.vOut : storeValue(interp(a.vOut, b.vIn, frac, ctx.info.valueType), ctx.info.valueType).value;
  keys.splice(i + 1, 0, newRec(t, v, v, a.ease));
  return i + 1;
}
